import {mat4, vec3, vec4, glMatrix} from 'gl-matrix';
import {ShaderLocationsVault} from "%COMMON/ShaderLocationsVault";

const CYLINDER_TOP_POINTS = 60;
const SPHERE_CIRCLE_POINTS = 60;
const SPHERE_NUM_SLICES = 60;
const NUM_BASE_PAIR_LAYERS = 19;
const DEGREES_PER_LAYER = 30;

/**
 * How the entire model is represented.
 */
export type Model = {
    name: string;
    items: ModelItem[];
}

/**
 * How each component of a model is represented
 */
export type ModelItem = {
    instance: ModelInstance; // vbo id 
    transformation: mat4;
    color: vec4;
}

/**
 * How to find the components for the model after they
 * are loaded into the GPU.
 */
export type ModelInstance = {
    vbo: WebGLBuffer;
    ibo: WebGLBuffer;
    numVertices: number;
    numIndices: number;
}

/**
 * A constructor for {@link Model}s
 * @param name The name for the model
 * @param items A list of {@link ModelItem}s which belong to this model
 */
export const createModel = (name: string, items: ModelItem[]): Model => {
    return {name, items}
};

/**
 * A constructor for ModelItems
 * @param instance The instance to use for this modelItem
 * @param transformation The transformation to apply to this modelItem
 * @param color the color to apply to this modelItem
 */
export const createModelItem = (instance: ModelInstance, transformation: mat4, color: vec4): ModelItem => {
    return {instance, transformation, color}
};


/**
 * Generates the structure containing a list of instances for our DNA model
 * @param gl The {@link WebGLRenderingContext} used to generate the model
 * @returns A {@link Model} representing our DNA model to be drawn
 */
export const generateDNAModel = (gl: WebGLRenderingContext): Model => {
    const C_COLOR = vec4.fromValues(1, .5, 0, 1);
    const A_COLOR = vec4.fromValues(1, 1, 0, 1);
    const G_COLOR = vec4.fromValues(1, 0, 0, 1);
    const T_COLOR = vec4.fromValues(0, 1, 0, 1);
    const BASE_COLOR = vec4.fromValues(92 / 255, 64 / 255, 51 / 255, 1);

    const origin = mat4.create();
    mat4.scale(origin, origin, vec3.fromValues(50, 50, 50));

    const pairLBaseMat = mat4.create();
    mat4.translate(pairLBaseMat, pairLBaseMat, vec3.fromValues(100, -680, 0))
    mat4.rotateY(pairLBaseMat, pairLBaseMat, glMatrix.toRadian(90))
    mat4.scale(pairLBaseMat, pairLBaseMat, vec3.fromValues(50, 50, 200));


    const pairRBaseMat = mat4.create();
    mat4.translate(pairRBaseMat, pairRBaseMat, vec3.fromValues(-100, -680, 0))
    mat4.rotateY(pairRBaseMat, pairRBaseMat, glMatrix.toRadian(90))
    mat4.scale(pairRBaseMat, pairRBaseMat, vec3.fromValues(50, 50, 200));

    const endcapLBaseMat = mat4.create();
    mat4.translate(endcapLBaseMat, endcapLBaseMat, vec3.fromValues(-200, -680, 0))
    mat4.scale(endcapLBaseMat, endcapLBaseMat, vec3.fromValues(75, 75, 75))

    const endcapRBaseMat = mat4.create();
    mat4.translate(endcapRBaseMat, endcapRBaseMat, vec3.fromValues(200, -680, 0))
    mat4.scale(endcapRBaseMat, endcapRBaseMat, vec3.fromValues(75, 75, 75))

    const plateBaseMat = mat4.create();
    mat4.translate(plateBaseMat, plateBaseMat, vec3.fromValues(0, -750, 0))
    mat4.scale(plateBaseMat, plateBaseMat, vec3.fromValues(450, 10, 450));

    const plateTopMat = mat4.create();
    mat4.translate(plateTopMat, plateTopMat, vec3.fromValues(0, 750, 0))
    mat4.scale(plateTopMat, plateTopMat, vec3.fromValues(450, 10, 450));

    const columnFRMat = mat4.create();
    mat4.translate(columnFRMat, columnFRMat, vec3.fromValues(200, 0, 200))
    mat4.scale(columnFRMat, columnFRMat, vec3.fromValues(10, 1500, 10))

    const columnFLMat = mat4.create();
    mat4.translate(columnFLMat, columnFLMat, vec3.fromValues(-200, 0, 200))
    mat4.scale(columnFLMat, columnFLMat, vec3.fromValues(10, 1500, 10))

    const columnBRMat = mat4.create();
    mat4.translate(columnBRMat, columnBRMat, vec3.fromValues(200, 0, -200))
    mat4.scale(columnBRMat, columnBRMat, vec3.fromValues(10, 1500, 10))

    const columnBLMat = mat4.create();
    mat4.translate(columnBLMat, columnBLMat, vec3.fromValues(-200, 0, -200))
    mat4.scale(columnBLMat, columnBLMat, vec3.fromValues(10, 1500, 10))

    const cyl = generateCylinderInstance(gl);
    const sphere = generateSphereInstance(gl);
    const cube = generateCubeInstance(gl);
    const items: ModelItem[] = [];

    items.push(
        createModelItem(
            cube,
            plateBaseMat,
            BASE_COLOR
        ),
        createModelItem(
            cube,
            plateTopMat,
            BASE_COLOR
        ),
        createModelItem(
            cube,
            columnFRMat,
            BASE_COLOR
        ),
        createModelItem(
            cube,
            columnFLMat,
            BASE_COLOR
        ),
        createModelItem(
            cube,
            columnBLMat,
            BASE_COLOR
        ),
        createModelItem(
            cube,
            columnBRMat,
            BASE_COLOR
        ),
    )

    for (let i = 0; i < NUM_BASE_PAIR_LAYERS; i++) {
        const layerTransform = mat4.create()
        mat4.translate(layerTransform, layerTransform, vec3.fromValues(0, 75 * i, 0));
        mat4.rotateY(layerTransform, layerTransform, glMatrix.toRadian(i * DEGREES_PER_LAYER));

        const pairL = createModelItem(
            cyl,
            mat4.multiply(mat4.create(), layerTransform, pairLBaseMat),
            (i % 2) == 0 ? A_COLOR : G_COLOR
        );
        const pairR = createModelItem(
            cyl,
            mat4.multiply(mat4.create(), layerTransform, pairRBaseMat),
            (i % 2) == 0 ? T_COLOR : C_COLOR
        );
        const capL = createModelItem(
            sphere,
            mat4.multiply(mat4.create(), layerTransform, endcapLBaseMat),
            vec4.fromValues(0, 0, 1, 1)
        )
        const capR = createModelItem(
            sphere,
            mat4.multiply(mat4.create(), layerTransform, endcapRBaseMat),
            vec4.fromValues(0, 0, 1, 1)
        )

        items.push(capL, pairL, pairR, capR);
    }

    return createModel("DNA", items);
};

/**
 * This function generates a unit cube centered at the origin. Tis means the the largest value for a particular axis
 * will be |.5|
 * @param gl the {@link WebGLRenderingContext} to create the instance within
 * @returns a {@link ModelInstance} containing the newly generated cube instance
 */
export const generateCubeInstance = (gl: WebGLRenderingContext): ModelInstance => {
    // Instantiate the buffers
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const vertices: number[] = [];

    /*        
    Looking as if x,y is 2d and z coming out.

    (-0.5, -0.5, 0.5) Front (z) BL
    (0.5, -0.5, 0.5) Front (z) BR
    (0.5, 0.5, 0.5) Front (z) TR
    (-0.5, 0.5, 0.5) Front (z) TL
    (-0.5, -0.5, -0.5) Back (z) BL
    (0.5, -0.5, -0.5) Back (z) BR
    (0.5, 0.5, -0.5) Back (z) TR
    (-0.5, 0.5, -0.5) Back (z) TL
    */

    vertices.push(-0.5, -0.5, 0.5);  // FBL IDX: 0
    vertices.push(0.5, -0.5, 0.5);   // FBR IDX: 1
    vertices.push(0.5, 0.5, 0.5);    // FTR IDX: 2
    vertices.push(-0.5, 0.5, 0.5);   // FTL IDX: 3
    vertices.push(-0.5, -0.5, -0.5); // BBL IDX: 4
    vertices.push(0.5, -0.5, -0.5);  // BBR IDX: 5
    vertices.push(0.5, 0.5, -0.5);   // BTR IDX: 6
    vertices.push(-0.5, 0.5, -0.5);  // BTL IDX: 7

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

    // Indices
    const indices: number[] = [];

    /*

    Front Face    (0, 2, 3), (0, 1, 2)
    Left Face     (1, 6, 2), (1, 5, 6)
    Back Face     (4, 5, 6), (4, 6, 7)
    Right Face    (4, 0, 3), (4, 3, 7)
    
    (Top Face)    (3, 2, 6), (3, 6, 7)
    (Bottom Face) (0, 1, 5), (0, 5, 4)

    */

    indices.push(0, 2, 3, 0, 1, 2); // front
    indices.push(1, 6, 2, 1, 5, 6); // left
    indices.push(4, 5, 6, 4, 6, 7); // back
    indices.push(4, 0, 3, 4, 3, 7); // right

    indices.push(3, 2, 6, 3, 6, 7); // top
    indices.push(0, 1, 5, 0, 5, 4); // bottom

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {vbo, ibo, numVertices: 8, numIndices: 32};
}

export const generateCylinderInstance = (gl: WebGLRenderingContext): ModelInstance => {
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    // Top circle indices will be even, bottom odd
    const vertices = [];
    const indices = [];
    for (let i = 0; i < CYLINDER_TOP_POINTS; i++) {
        const theta = 2 * Math.PI * i / CYLINDER_TOP_POINTS;
        const x = 0.5 * Math.cos(theta);
        const y = 0.5 * Math.sin(theta);
        vertices.push(x, y, 0.5, x, y, -0.5);
        const topIndex = i * 2;
        const bottomIndex = (i * 2) + 1;
        const nextI = (i + 1) % CYLINDER_TOP_POINTS;
        const nextTopIndex = nextI * 2;
        const nextBottomIndex = (nextI * 2) + 1;
        indices.push(topIndex, bottomIndex, nextTopIndex);
        indices.push(bottomIndex, nextBottomIndex, nextTopIndex);
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {vbo, ibo, numVertices: CYLINDER_TOP_POINTS * 6, numIndices: CYLINDER_TOP_POINTS * 6};
}

/**
 * This function generates a unit sphere centered at the origin. This means that the points intersecting the x, y, and z
 * axes are at most |.5|
 * @returns a {@link ModelInstance} containing the instance with the unit circle
 * @param gl the {@link WebGLRenderingContext} we want to create the sphere within
 */
export const generateSphereInstance = (gl: WebGLRenderingContext): ModelInstance => {
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const r = 0.5;
    const vertices = [];
    const indices = [];
    const topPoint = 0;
    vertices.push(0, 0, 0.5); // top point
    // draw the top cap
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + 1;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + 1;

        indices.push(topPoint, circlePoint, circleNextPoint);
    }
    for (let s = 1; s < SPHERE_NUM_SLICES; s++) {
        const z = 0.5 - (s / SPHERE_NUM_SLICES);
        const sliceR = Math.sqrt(r * r - z * z); // radius of the slice
        const circleFirstPoint = 1 + SPHERE_CIRCLE_POINTS * (s - 1);
        const nextCircleFirstPoint = circleFirstPoint + SPHERE_CIRCLE_POINTS;
        for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
            const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
            const x = sliceR * Math.cos(theta);
            const y = sliceR * Math.sin(theta);
            vertices.push(x, y, z);

            if (s !== SPHERE_NUM_SLICES - 1) {
                const point = i;
                const nextPoint = (i + 1) % SPHERE_CIRCLE_POINTS;
                const circlePoint = circleFirstPoint + point;
                const circleNextPoint = circleFirstPoint + nextPoint;
                const nextCirclePoint = nextCircleFirstPoint + point;
                const nextCircleNextPoint = nextCircleFirstPoint + nextPoint;

                indices.push(circlePoint, nextCirclePoint, circleNextPoint);
                indices.push(nextCirclePoint, nextCircleNextPoint, circleNextPoint);
            }
        }
    }
    // Draw the bottom point
    const bottomPoint = vertices.length / 3;
    vertices.push(0, 0, -0.5);
    // Draw the bottom cap
    const lastSliceFirstPoint = bottomPoint - SPHERE_CIRCLE_POINTS;
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + lastSliceFirstPoint;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + lastSliceFirstPoint;

        indices.push(bottomPoint, circlePoint, circleNextPoint);
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
        vbo,
        ibo,
        numVertices: /*CYLINDER_TOP_POINTS * 6*/vertices.length / 3,
        numIndices: /*CYLINDER_TOP_POINTS * 6*/indices.length
    };
};

/**
 * Draws all modelItems in the model
 * @param gl the {@link WebGLRenderingContext} to render to
 * @param shaderLocations a {@link ShaderLocationsVault} containing all of the shader locations
 * @param model the {@link Model} whose items we will draw
 * @param modelView the {@link mat4} containing the modelView matrix
 * @param proj the {@link mat4} containing the projection matrix
 */
export const drawModel = (gl: WebGLRenderingContext, shaderLocations: ShaderLocationsVault, model: Model, modelView: mat4, proj: mat4): void => {

    const modelViewLoc = shaderLocations.getUniformLocation("modelview");
    const projLoc = shaderLocations.getUniformLocation("proj");
    const colorLoc = shaderLocations.getUniformLocation("vColor");
    const posLoc = shaderLocations.getAttribLocation("vPosition");
    model.items.forEach(m => drawModelItem(
        m,
        gl,
        modelViewLoc,
        modelView,
        colorLoc,
        proj,
        projLoc,
        posLoc
    ));

}

/**
 * Handles drawing individual {@link ModelItem}s to the screen
 * @param model The {@link ModelItem} to draw
 * @param gl the {@link WebGLRenderingContext} to draw to
 * @param modelViewLoc the location of the modelViewUniform
 * @param modelViewMat the {@link mat4 } containing the modelview
 * @param colorLoc the location of the color uniform
 * @param projectionMat the {@link mat4 } containing the projection matrix
 * @param projectionLoc the location of the projection uniform
 * @param positionLoc the location of the vPosition attribute
 */
const drawModelItem = (model: ModelItem, gl: WebGLRenderingContext, modelViewLoc: WebGLUniformLocation, modelViewMat: mat4, colorLoc: WebGLUniformLocation, projectionMat: mat4, projectionLoc: WebGLUniformLocation, positionLoc: number): void => {
    gl.bindBuffer(gl.ARRAY_BUFFER, model.instance.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, model.instance.ibo);
    const mat = mat4.multiply(mat4.create(), modelViewMat, model.transformation);
    gl.uniformMatrix4fv(modelViewLoc, false, mat);
    gl.uniform4fv(colorLoc, model.color);// set color uniform for ModelItem
    gl.uniformMatrix4fv(projectionLoc, false, projectionMat);
    gl.vertexAttribPointer(positionLoc, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(positionLoc);
    gl.drawElements(gl.TRIANGLES, model.instance.numIndices, gl.UNSIGNED_SHORT, 0);
}
    



